/**
 * InlineEntityInput (Web) — Type-to-search or type-to-create entity combo input.
 * Uses Convex queries/mutations for entity search and creation.
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { Plus, Search, Loader2, X } from 'lucide-react';

const ENTITY_TYPE_META: Record<string, { icon: string; label: string }> = {
  dj: { icon: '🎧', label: 'DJ' },
  band: { icon: '🎸', label: 'Band' },
  singer: { icon: '🎤', label: 'Singer' },
  mc: { icon: '🎙️', label: 'MC' },
  performer: { icon: '🎤', label: 'Performer' },
  venue: { icon: '🏛️', label: 'Venue' },
  promoter: { icon: '📢', label: 'Promoter' },
  sponsor: { icon: '💎', label: 'Sponsor' },
};

type InlineEntityInputProps = {
  defaultType?: string;
  placeholder?: string;
  onSelect: (entity: { _id: string; name: string; entityType: string }) => void;
  excludeIds?: string[];
  filterTypes?: string[];
  compact?: boolean;
};

export function InlineEntityInput({
  defaultType = 'dj',
  placeholder = 'Search or add...',
  onSelect,
  excludeIds = [],
  filterTypes,
  compact = false,
}: InlineEntityInputProps) {
  const entities = useQuery(api.entities.list, {});
  const createEntity = useMutation(api.entities.create);

  const [query, setQuery] = useState('');
  const [isOpen, setIsOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const results = (() => {
    if (!query.trim() || !entities) return [];
    const q = query.toLowerCase().trim();
    return entities
      .filter((e) => {
        if (excludeIds.includes(e._id)) return false;
        if (filterTypes && !filterTypes.includes(e.entityType)) return false;
        return e.name.toLowerCase().includes(q);
      })
      .slice(0, 8);
  })();

  useEffect(() => {
    setHighlightIdx(0);
  }, [query]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (
        dropdownRef.current && !dropdownRef.current.contains(e.target as Node) &&
        inputRef.current && !inputRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const handleSelect = useCallback(
    (entity: { _id: string; name: string; entityType: string }) => {
      onSelect(entity);
      setQuery('');
      setIsOpen(false);
    },
    [onSelect],
  );

  const handleCreate = useCallback(async () => {
    if (!query.trim()) return;
    setIsCreating(true);
    try {
      const id = await createEntity({
        entityType: defaultType,
        name: query.trim(),
      });
      onSelect({ _id: id, name: query.trim(), entityType: defaultType });
      setQuery('');
      setIsOpen(false);
    } catch (err) {
      console.error('InlineEntityInput: Failed to create entity', err);
    } finally {
      setIsCreating(false);
    }
  }, [query, defaultType, createEntity, onSelect]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') setIsOpen(true);
      return;
    }
    const totalItems = results.length + (query.trim() ? 1 : 0);
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setHighlightIdx((i) => Math.min(totalItems - 1, i + 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setHighlightIdx((i) => Math.max(0, i - 1));
        break;
      case 'Enter':
        e.preventDefault();
        if (highlightIdx < results.length) {
          handleSelect(results[highlightIdx]);
        } else if (query.trim()) {
          handleCreate();
        }
        break;
      case 'Escape':
        setIsOpen(false);
        break;
    }
  };

  const typeMeta = ENTITY_TYPE_META[defaultType] ?? { icon: '✨', label: defaultType };
  const showDropdown = isOpen && (results.length > 0 || query.trim());

  return (
    <div className="relative">
      <div
        className={`flex items-center gap-2 border border-input rounded-lg bg-background transition-colors focus-within:ring-1 focus-within:ring-ring ${
          compact ? 'px-2 py-1' : 'px-3 py-2'
        }`}
      >
        <Search className={`text-muted-foreground flex-shrink-0 ${compact ? 'w-3 h-3' : 'w-3.5 h-3.5'}`} />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value); setIsOpen(true); }}
          onFocus={() => query.trim() && setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className={`flex-1 bg-transparent placeholder:text-muted-foreground/60 focus:outline-none min-w-0 ${
            compact ? 'text-xs' : 'text-sm'
          }`}
        />
        {query && (
          <button onClick={() => { setQuery(''); setIsOpen(false); }} className="p-0.5 rounded hover:bg-muted text-muted-foreground">
            <X className="w-3 h-3" />
          </button>
        )}
      </div>

      {showDropdown && (
        <div ref={dropdownRef} className="absolute z-50 mt-1 w-full rounded-lg border border-border bg-popover shadow-lg overflow-hidden">
          {results.map((entity, idx) => {
            const meta = ENTITY_TYPE_META[entity.entityType];
            return (
              <button
                key={entity._id}
                onClick={() => handleSelect(entity)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors ${
                  idx === highlightIdx ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50'
                }`}
              >
                <span className="text-sm">{meta?.icon ?? '✨'}</span>
                <div className="flex-1 min-w-0">
                  <p className={`font-medium truncate ${compact ? 'text-xs' : 'text-sm'}`}>{entity.name}</p>
                  <p className="text-[10px] text-muted-foreground">{meta?.label ?? entity.entityType}</p>
                </div>
              </button>
            );
          })}

          {query.trim() && (
            <button
              onClick={handleCreate}
              disabled={isCreating}
              className={`w-full flex items-center gap-2.5 px-3 py-2 text-left border-t border-border transition-colors ${
                highlightIdx === results.length ? 'bg-accent text-accent-foreground' : 'hover:bg-muted/50'
              }`}
            >
              {isCreating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin text-[#25a48f]" />
              ) : (
                <Plus className="w-3.5 h-3.5 text-[#25a48f]" />
              )}
              <div className="flex-1 min-w-0">
                <p className={`font-medium truncate ${compact ? 'text-xs' : 'text-sm'}`}>Create "{query.trim()}"</p>
                <p className="text-[10px] text-muted-foreground">as {typeMeta.label}</p>
              </div>
            </button>
          )}
        </div>
      )}
    </div>
  );
}
